import { ZfStack, ZfTextButton } from '@zafire/react';
import type Highcharts from 'highcharts/highstock';
import type { FunctionComponent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCashFlow, useGeneralProjectionSettings } from '../../api';
import { CashFlowChartLegend } from '../../components/projection/chart/CashFlowChartLegend';
import { ChartWrapper } from '../../components/projection/chart/ChartWrapper';
import { SimulationSettingsDrawer } from '../../components/simulation-settings';
import { DEFAULT_TIME_RANGE, SETTINGS_DRAWER_WIDTH } from '../../config/constants';
import { useLayout } from '../../contexts/layout-context';
import { useCashflowChartCallbacks } from '../../hooks/chart/useCashflowChartCallbacks';
import { useCashFlowSeriesForClientOutput } from '../../hooks/chart/useCashFlowSeriesForClientOutput';
import { useElementHeight } from '../../hooks/useElementHeight';

export const CashFlowChartScreen: FunctionComponent = () => {
    const { t } = useTranslation();
    const { totalNavbarHeight } = useLayout();

    const { data: cashFlowProjection, isSuccess } = useCashFlow();
    const { data: generalProjectionSettings } = useGeneralProjectionSettings();
    const { seriesData } = useCashFlowSeriesForClientOutput(cashFlowProjection);
    const { addColumnPadding } = useCashflowChartCallbacks();

    const headerRef = useRef<HTMLDivElement>(null);
    const [chart, setChart] = useState<Highcharts.Chart | undefined>(undefined);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const headerHeight = useElementHeight(headerRef, [isDrawerOpen]);

    const handleOpenDrawer = () => setIsDrawerOpen(true);
    const handleCloseDrawer = () => setIsDrawerOpen(false);

    return (
        <div className="d-flex flex-1" style={{ width: '100%', overflow: 'hidden', height: '100%' }}>
            <ZfStack className="projection-screen__wrapper" direction="row" justify="flex-start" style={{ width: '100%', maxWidth: '100%', height: '100%' }}>
                <div
                    className="d-flex fd-column"
                    style={{ width: !isDrawerOpen ? '100%' : `calc(100% - ${SETTINGS_DRAWER_WIDTH}px)`, flexShrink: 1, minWidth: 0 }}
                >
                    <div className="cash-flow-chart__header" ref={headerRef}>
                        <h3>{t('cashflow')}</h3>
                        <div className="cash-flow-chart__subtitle">
                            <CashFlowChartLegend chart={chart} seriesData={seriesData} />
                            <ZfTextButton
                                size="medium"
                                onClick={() => (isDrawerOpen ? handleCloseDrawer() : handleOpenDrawer())}
                                hierarchy="secondary"
                                disabled={!isSuccess}
                            >
                                {t('simulationSettings')}
                            </ZfTextButton>
                        </div>
                    </div>
                    <div
                        className="chart-container"
                        style={{
                            height: `calc(100vh - ${totalNavbarHeight + headerHeight}px)`,
                            width: '100%',
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        <div className="chart-container__chart">
                            <ChartWrapper
                                chartType="CASH_FLOW"
                                onChartRenderEventHandlers={[addColumnPadding]}
                                seriesData={seriesData}
                                setChart={setChart}
                                isDrawerOpen={isDrawerOpen}
                                projectionPeriodYears={generalProjectionSettings?.projectionPeriodYears || DEFAULT_TIME_RANGE}
                            />
                        </div>
                    </div>
                </div>
                <SimulationSettingsDrawer chartType="CASH_FLOW" isDrawerOpen={isDrawerOpen} onCloseDrawer={handleCloseDrawer} />
            </ZfStack>
        </div>
    );
};
